import { useState, useEffect } from "react";

import { Bell, BellOff, Calendar } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";


type NotificationPreference = "instant" | "daily_digest" | "disabled";

export const NotificationPreferences = () => {
  const { user } = useAuth();
  const [preference, setPreference] = useState<NotificationPreference>("instant");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPreference();
  }, [user]);

  const fetchPreference = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("admin_preferences")
        .select("notification_preference")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 is "no rows returned" - that's okay
        console.error("Error fetching preference:", error);
      } else if (data) {
        setPreference(data.notification_preference as NotificationPreference);
      }
    } catch (error) {
      console.error("Error fetching preference:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = async (value: string) => {
    if (!user) return;

    const newPreference = value as NotificationPreference;
    setPreference(newPreference);
    setSaving(true);

    try {
      // Try to update first
      const { error: updateError } = await supabase
        .from("admin_preferences")
        .update({ notification_preference: newPreference })
        .eq("user_id", user.id);

      // If update fails (no row exists), insert instead
      if (updateError) {
        const { error: insertError } = await supabase
          .from("admin_preferences")
          .insert({
            user_id: user.id,
            notification_preference: newPreference,
          });

        if (insertError) throw insertError;
      }

      toast.success("Notification preferences updated");
    } catch (error: any) {
      console.error("Error saving preference:", error);
      toast.error("Failed to update preferences");
      // Revert on error
      fetchPreference();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>Loading...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Choose how you want to receive waitlist notifications
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={preference}
          onValueChange={handlePreferenceChange}
          disabled={saving}
          className="space-y-4"
        >
          <div className="flex items-start space-x-3 p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors">
            <RadioGroupItem value="instant" id="instant" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="instant" className="flex items-center gap-2 font-medium cursor-pointer">
                <Bell className="h-4 w-4" />
                Instant Notifications
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Get an email immediately when someone joins the waitlist
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors">
            <RadioGroupItem value="daily_digest" id="daily_digest" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="daily_digest" className="flex items-center gap-2 font-medium cursor-pointer">
                <Calendar className="h-4 w-4" />
                Daily Digest
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Receive a daily summary of new signups and approvals (sent once per day if there's activity)
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors">
            <RadioGroupItem value="disabled" id="disabled" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="disabled" className="flex items-center gap-2 font-medium cursor-pointer">
                <BellOff className="h-4 w-4" />
                Disabled
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Don't send any notification emails
              </p>
            </div>
          </div>
        </RadioGroup>
      </CardContent>
    </Card>
  );
};
